import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import './styles/design-system.css';
import {
  resolveLeaderboardDisplayName,
  getLeaderboardAvatarInitials,
  shouldHideLeaderboardPhoto,
  type ClientPrivacyFields,
} from './utils/clientPrivacy';
import { checkSubscriptionStatus } from './utils/subscriptionUtils';
import { filterToAllowedClients, getClientAccessInfo } from './utils/clientAccess';
import {
  calculateLoggingStreak,
  calculateSuccessRate,
  calculateDaysSinceLastMeal,
  type MealRow,
  type DietGoals,
} from './utils/clientStatsUtils';

interface LeaderboardEntry {
  clientName: string;
  leaderboardDisplayName: string;
  avatarInitials: string;
  hidePhoto: boolean;
  telegramUserId: string;
  successRate: number;
  currentStreak: number;
  totalMeals: number;
  daysSinceLastMeal: number;
  profilePictureUrl?: string;
}

export default function ClientLeaderboard() {
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLeaderboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get current trainer's clients
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: clients, error: clientsError } = await supabase
        .from('trainers_clients')
        .select('*')
        .eq('trainer_id', user.id);

      if (clientsError) {
        console.error('Error fetching clients:', clientsError);
        return;
      }

      const isPremium = await checkSubscriptionStatus(user.id);
      const { allowedTelegramIds } = await getClientAccessInfo(user.id, isPremium);
      const visibleClients = filterToAllowedClients(clients || [], allowedTelegramIds);

      // Get leaderboard data for each active client
      const leaderboardEntries: LeaderboardEntry[] = [];

      for (const client of visibleClients) {
        // Get client's meals
        const { data: meals, error: mealsError } = await supabase
          .from('Mastersheet')
          .select('*')
          .eq('telegram_user_id', client.telegram_user_id)
          .order('meal_time', { ascending: false });

        if (mealsError) {
          console.error('Error fetching meals for client:', client.telegram_user_id, mealsError);
          continue;
        }

        // Get client's diet goals
        const { data: dietGoals, error: goalsError } = await supabase
          .from('client_diet_goals')
          .select('*')
          .eq('telegram_user_id', client.telegram_user_id)
          .single();

        if (goalsError && goalsError.code !== 'PGRST116') {
          console.error('Error fetching diet goals for client:', client.telegram_user_id, goalsError);
          continue;
        }

        // Get client's profile and privacy settings
        const { data: profileData, error: profileError } = await supabase
          .from('client_profiles')
          .select('profile_picture_url, display_name, social_privacy_setting')
          .eq('telegram_user_id', client.telegram_user_id)
          .single();

        if (profileError && profileError.code !== 'PGRST116') {
          console.error('Error fetching profile for client:', client.telegram_user_id, profileError);
        }

        const clientName = client.client_name || `User: ${client.telegram_user_id}`;
        const privacy: ClientPrivacyFields = {
          display_name: profileData?.display_name || clientName,
          social_privacy_setting: profileData?.social_privacy_setting || 'initials',
        };

        const mealRows = (meals || []) as MealRow[];
        const currentStreak = calculateLoggingStreak(mealRows);
        const daysSinceLastMeal = calculateDaysSinceLastMeal(mealRows);
        const successRate = dietGoals
          ? calculateSuccessRate(mealRows, dietGoals as DietGoals)
          : 0;

        leaderboardEntries.push({
          clientName,
          leaderboardDisplayName: resolveLeaderboardDisplayName(clientName, privacy),
          avatarInitials: getLeaderboardAvatarInitials(clientName, privacy),
          hidePhoto: shouldHideLeaderboardPhoto(privacy),
          telegramUserId: client.telegram_user_id,
          successRate,
          currentStreak,
          totalMeals: mealRows.length,
          daysSinceLastMeal,
          profilePictureUrl: isPremium ? (profileData?.profile_picture_url || undefined) : undefined
        });
      }

      leaderboardEntries.sort((a, b) => {
        if (b.currentStreak !== a.currentStreak) return b.currentStreak - a.currentStreak;
        if (b.successRate !== a.successRate) return b.successRate - a.successRate;
        return b.totalMeals - a.totalMeals;
      });

      setLeaderboardData(leaderboardEntries);
    } catch (error) {
      console.error('Error fetching leaderboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch leaderboard data
  useEffect(() => {
    fetchLeaderboardData();
  }, [fetchLeaderboardData]);

  const getRankColor = (rank: number) => {
    if (rank === 1) return 'text-yellow-600';
    if (rank === 2) return 'text-gray-500';
    if (rank === 3) return 'text-amber-700';
    return 'text-gray-400';
  };

  if (loading) {
    return (
      <div className="retro-card">
        <div className="index-tab">
          <h3 className="text-lg font-semibold text-[#1f1f1f]">🏆 Client Leaderboard</h3>
        </div>
        <div className="p-4 md:p-6">
          <div className="text-center py-8 text-[#2d2d2d]">Loading leaderboard...</div>
        </div>
      </div>
    );
  }

  if (leaderboardData.length === 0) {
    return (
      <div className="retro-card">
        <div className="index-tab">
          <h3 className="text-lg font-semibold text-[#1f1f1f]">🏆 Client Leaderboard</h3>
        </div>
        <div className="p-4 md:p-6">
          <div className="text-center py-8 text-[#2d2d2d]">
            No clients found. Add some clients to see the leaderboard!
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="retro-card">
      <div className="index-tab">
        <h3 className="text-lg font-semibold text-[#1f1f1f]">🏆 Client Leaderboard</h3>
        <p className="text-xs text-[#666] mt-1 px-4">Ranked by consistency streak and goal success</p>
      </div>
      <div className="p-4 md:p-6">
        <div className="space-y-3">
          {leaderboardData.map((entry, index) => (
            <div 
              key={entry.telegramUserId}
              className="bg-white rounded-lg shadow p-4 border border-[#d4d4d4] hover:bg-[#f8f4e6] transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`text-2xl font-bold ${getRankColor(index + 1)}`}>
                    {index + 1}
                  </div>
                  {entry.profilePictureUrl && !entry.hidePhoto ? (
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full overflow-hidden border border-[#d4d4d4]">
                      <img 
                        src={entry.profilePictureUrl} 
                        alt={`${entry.leaderboardDisplayName}'s profile`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback to initials if image fails to load
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                      <div className="w-full h-full bg-gradient-to-br from-[#0eeaff] to-[#8aff8a] rounded-full flex items-center justify-center text-white font-bold text-xs hidden">
                        {entry.avatarInitials}
                      </div>
                    </div>
                  ) : (
                    <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-[#0eeaff] to-[#8aff8a] rounded-full flex items-center justify-center text-white font-bold text-xs">
                      {entry.avatarInitials}
                    </div>
                  )}
                  <div>
                    <h4 className="font-semibold text-[#1f1f1f] text-sm md:text-base">
                      {entry.leaderboardDisplayName}
                    </h4>
                    <p className="text-xs text-[#666]">
                      {entry.totalMeals} meals logged
                      {entry.daysSinceLastMeal > 0 && entry.daysSinceLastMeal < 999 && (
                        <span className="ml-1 text-[#ff6b6b]">
                          • {entry.daysSinceLastMeal} {entry.daysSinceLastMeal === 1 ? 'day' : 'days'} ago
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="text-center min-w-[3rem]">
                    <div className="text-lg font-bold text-[#0eeaff]">{entry.currentStreak}</div>
                    <div className="text-xs text-[#666]">day streak</div>
                  </div>

                  <div className="text-center min-w-[3rem]">
                    <div className={`text-lg font-bold ${
                      entry.successRate >= 90 ? 'text-[#8aff8a]' :
                      entry.successRate >= 70 ? 'text-[#ffd700]' : 'text-[#ff6b6b]'
                    }`}>
                      {entry.successRate}%
                    </div>
                    <div className="text-xs text-[#666]">on target</div>
                  </div>

                  <div className="text-center min-w-[3rem]">
                    <div className="text-lg font-bold text-[#666]">{entry.totalMeals}</div>
                    <div className="text-xs text-[#666]">meals</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-6 p-4 bg-[#E6F4FE] rounded-lg">
          <p className="text-sm font-semibold text-[#1f1f1f] mb-1">💡 Leaderboard Rankings</p>
          <p className="text-sm text-[#666]">
            Streak counts consecutive logging days — if a client hasn't logged today yet, yesterday still counts.
            On target is a 30-day macro success average.
          </p>
        </div>
      </div>
    </div>
  );
} 